import { IUser } from 'interfaces/IUser';

const isEnabledUserAttribute = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => isEnabledUserAttribute(item));
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return value === true;
};

const isSpecializedAccount = (
  user: Partial<IUser> | null | undefined,
): boolean =>
  Boolean(
    user?.quickcodeOnly ||
      user?.bite_super_admin ||
      isEnabledUserAttribute(user?.basics?.attributes?.quickcode),
  );

/**
 * Team creation is part of the first-login flow. Keep this check independent
 * from selected_team because new users can temporarily carry the EMPTY_TEAM
 * placeholder while still having no real team membership.
 */
export const requiresTeamOnboarding = (
  user: Partial<IUser> | null | undefined,
): boolean =>
  Boolean(user?.basics?.id) &&
  Array.isArray(user?.teams) &&
  user.teams.length === 0 &&
  !user?.awsUser &&
  !isSpecializedAccount(user);

/**
 * The homepage must not run team-scoped queries when a member's active team
 * could not be restored. This is separate from first-login team creation.
 */
export const isMissingActiveTeamSelection = (
  user: Partial<IUser> | null | undefined,
): boolean => {
  if (
    !user?.basics?.id ||
    !Array.isArray(user.teams) ||
    user.teams.length === 0 ||
    user.awsUser
  ) {
    return false;
  }

  const selectedTeamId = user.selected_team?.id;
  return (
    !selectedTeamId ||
    selectedTeamId === 'EMPTY_TEAM' ||
    !user.teams.some((team) => team.id === selectedTeamId)
  );
};

/**
 * A teamless user must still be able to accept an invitation instead of being
 * forced to create a new team.
 */
export const isTeamInviteRoute = (pathname: string): boolean =>
  /^\/join-team\/[^/]+\/?$/.test(pathname);

export const isTeamOnboardingRoute = (pathname: string): boolean =>
  pathname === '/' || isTeamInviteRoute(pathname);

/**
 * Logged-out BITE users normally return to the homepage after authentication so
 * first-login onboarding cannot be bypassed. Team invitations are the one safe
 * deep link that must survive authentication.
 */
export const getBiteLoginRedirectUri = (url: URL): string =>
  isTeamInviteRoute(url.pathname)
    ? `${url.origin}${url.pathname}${url.search}`
    : url.origin;
